import React, { useCallback, useEffect, useState } from 'react';
import { Tooltip } from '@material-ui/core';
import synchronizeApi from 'api/synchronizeApi';
import cn from 'classnames';
import CustomDateRangePicker from 'components/Form/CommonDatePicker';
import Loading from 'components/Loading';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { openToast } from 'stores/toast/slice.toast';
import * as actionsFanpage from 'views/Fanpage/FanpageActions';
import { fanpageFull } from 'views/Fanpage/FanpageSelectors';
import SingleSelectbox from '../../../components/Form/Selectbox/SingleSelectbox';
import MainLayout from '../../MainLayout';
import types from '../../../constants/types';
import { selectUser } from 'views/App/AppSelectors';
import SyncChart from './SyncChart';
import s from './SyncManagement.module.scss';

export const syncManager = {
  SYNC_CUSTOMER: 'SYNC_CUSTOMER',
  INTEGRATION_CODE: 'INTEGRATION_CODE',
};

const SYSTEMS = [{ value: 'pancake', label: 'Pancake' }];

/* ── tiny icons ── */
function SyncIcon({ size = 16, color = 'currentColor' }) {
  return (
    <svg viewBox="0 0 16 16" style={{ width: size, height: size, fill: 'none', stroke: color, strokeWidth: 1.8, flexShrink: 0 }}>
      <path d="M2.5 8a5.5 5.5 0 019.5-3.5M13.5 8a5.5 5.5 0 01-9.5 3.5" strokeLinecap="round" />
      <path d="M12 2v3h-3M4 11v3h3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 10 10" style={{ width: 8, height: 8, fill: 'none', stroke: '#fff', strokeWidth: 2.2 }}>
      <path d="M2 5.2l2.2 2.2L8 3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChartLineIcon({ size = 15, color = '#6B7280' }) {
  return (
    <svg viewBox="0 0 16 16" style={{ width: size, height: size, fill: 'none', stroke: color, strokeWidth: 1.8 }}>
      <polyline points="2,12 5,7 8,9 11,4 14,6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const SyncManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fanpages = useSelector(fanpageFull);
  const user = useSelector(selectUser);

  const [activeTab, setActiveTab] = useState(syncManager.SYNC_CUSTOMER);
  const [selectedFanpage, setSelectedFanpage] = useState(null);
  const [selectedSystem, setSelectedSystem] = useState(SYSTEMS[0]);
  const [accessToken, setAccessToken] = useState('');
  const [periodMode, setPeriodMode] = useState('ago');
  const [agoMonths, setAgoMonths] = useState(3);
  const [dateRange, setDateRange] = useState([null, null]);
  const [syncFields, setSyncFields] = useState({ email: false, phone: false, birth: false });
  const [loading, setLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    dispatch(actionsFanpage.getFanpageFull());
  }, [dispatch]);

  const fanpageOptions = (fanpages || []).map((fp) => ({
    value: fp.pageId,
    label: fp.name,
  }));

  const canSync = !!selectedFanpage && accessToken.trim().length > 0;

  const handleSync = useCallback(async () => {
    if (!canSync || loading) return;
    try {
      setLoading(true);
      const payload = {
        fanpageId: selectedFanpage.value,
        system: selectedSystem.value,
        accessToken: accessToken.trim(),
        periodMode,
        agoMonths: periodMode === 'ago' ? agoMonths : undefined,
        dateFrom: periodMode === 'range' && dateRange[0] ? moment(dateRange[0]).format('YYYY-MM-DD') : undefined,
        dateTo: periodMode === 'range' && dateRange[1] ? moment(dateRange[1]).format('YYYY-MM-DD') : undefined,
        syncFields: { tag: true, ...syncFields },
      };
      const res = await synchronizeApi.syncPancakeToBombot(payload);
      setSyncResult(res?.data || res || {});
      dispatch(openToast({ type: types.TOAST.SUCCESS, message: t('synchronize_management.toast.sync_successful') }));
    } catch (err) {
      dispatch(openToast({ type: types.TOAST.ERROR, message: err?.message || 'Lỗi đồng bộ' }));
    } finally {
      setLoading(false);
    }
  }, [canSync, loading, selectedFanpage, selectedSystem, accessToken, periodMode, agoMonths, dateRange, syncFields, dispatch, t]);

  const handleCopyToken = useCallback(() => {
    if (!user?.publicToken) return;
    navigator.clipboard.writeText(user.publicToken).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [user?.publicToken]);

  /* ── system options with green dot ── */
  const systemOptionsWithDot = SYSTEMS.map((sys) => ({
    value: sys.value,
    label: sys.label,
    icon: null,
    dotLabel: (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22C55E', display: 'inline-block', flexShrink: 0 }} />
        {sys.label}
      </span>
    ),
  }));

  const selectedSystemDisplay = selectedSystem
    ? { value: selectedSystem.value, label: selectedSystem.label }
    : null;

  return (
    <MainLayout>
      <div className={s.page}>

        {/* Breadcrumb */}
        <div className={s.breadcrumb}>
          Quản lý tài khoản&nbsp;/&nbsp;<strong>Tích hợp &amp; Đồng bộ</strong>
        </div>

        {/* Title */}
        <h1 className={s.pageTitle}>Tích hợp &amp; Đồng bộ</h1>
        <p className={s.pageSub}>
          Đồng bộ dữ liệu khách hàng từ các hệ thống quản lý fanpage bên ngoài, hoặc lấy mã tích hợp để kết nối Bombot với hệ thống của bạn qua API.
        </p>

        {/* Tabs */}
        <div className={s.tabBar}>
          <button
            className={cn(s.tabBtn, activeTab === syncManager.SYNC_CUSTOMER && s.tabBtnActive)}
            onClick={() => setActiveTab(syncManager.SYNC_CUSTOMER)}
          >
            <SyncIcon size={14} color={activeTab === syncManager.SYNC_CUSTOMER ? '#111827' : '#9CA3AF'} />
            Đồng bộ khách hàng
          </button>
          <button
            className={cn(s.tabBtn, activeTab === syncManager.INTEGRATION_CODE && s.tabBtnActive)}
            onClick={() => setActiveTab(syncManager.INTEGRATION_CODE)}
          >
            <svg viewBox="0 0 16 16" style={{ width: 14, height: 14, fill: 'none', stroke: activeTab === syncManager.INTEGRATION_CODE ? '#111827' : '#9CA3AF', strokeWidth: 1.8 }}>
              <path d="M5 4l-3 4 3 4M11 4l3 4-3 4M9 2l-2 12" strokeLinecap="round" />
            </svg>
            Mã tích hợp
            <span className={s.proBadge}>PRO</span>
          </button>
        </div>

        {/* ══ TAB: ĐỒNG BỘ KHÁCH HÀNG ══ */}
        {activeTab === syncManager.SYNC_CUSTOMER && (
          <div className={s.twoCol}>

            {/* ── LEFT card: form ── */}
            <div className={s.card}>
              {/* Card header */}
              <div className={s.cardHd}>
                <div className={s.cardHdIconWrap}>
                  <svg viewBox="0 0 16 16" style={{ width: 14, height: 14, fill: 'none', stroke: '#16A34A', strokeWidth: 1.8 }}>
                    <circle cx="8" cy="5.5" r="3" />
                    <path d="M2.5 14c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <div className={s.cardHdTitle}>Thông tin đồng bộ người dùng</div>
                  <div className={s.cardHdSub}>Điền đầy đủ thông tin bên dưới để bắt đầu đồng bộ khách hàng từ fanpage</div>
                </div>
              </div>

              {/* Card body */}
              <div className={s.cardBd}>

                {/* Fanpage */}
                <div className={s.field}>
                  <div className={s.fieldLabel}>
                    Fanpage <span className={s.req}>*</span>
                  </div>
                  <SingleSelectbox
                    options={fanpageOptions}
                    selectedValue={selectedFanpage}
                    onChange={setSelectedFanpage}
                    placeholder="Chọn fanpage"
                  />
                </div>

                {/* Hệ thống */}
                <div className={s.field}>
                  <div className={s.fieldLabel}>
                    Hệ thống <span className={s.req}>*</span>
                  </div>
                  {/* Custom system dropdown with green dot */}
                  <div
                    style={{
                      border: '1px solid #D1D5DB',
                      borderRadius: 8,
                      padding: '9px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'default',
                      background: '#fff',
                      fontSize: 13,
                      color: '#111827',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22C55E', display: 'inline-block', flexShrink: 0 }} />
                      Pancake
                    </span>
                    <svg viewBox="0 0 12 12" style={{ width: 12, height: 12, fill: 'none', stroke: '#9CA3AF', strokeWidth: 2 }}>
                      <path d="M2 4.5l4 4 4-4" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>

                {/* Access Token */}
                <div className={s.field}>
                  <div className={s.fieldLabel}>
                    Access Token <span className={s.req}>*</span>
                    <Tooltip title="Lấy Access Token trong phần cài đặt hệ thống bạn chọn ở trên" placement="top">
                      <button className={s.helpBtn}>
                        <svg viewBox="0 0 16 16" style={{ width: 15, height: 15, fill: 'none', stroke: '#9CA3AF', strokeWidth: 1.8 }}>
                          <circle cx="8" cy="8" r="6" />
                          <path d="M8 5.5v2.5" strokeLinecap="round" />
                          <circle cx="8" cy="11" r="0.6" fill="#9CA3AF" stroke="none" />
                        </svg>
                      </button>
                    </Tooltip>
                  </div>
                  <textarea
                    className={s.tokenArea}
                    placeholder="Nhập Access token"
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                  />
                </div>

                {/* Khoảng thời gian */}
                <div className={s.field}>
                  <div className={s.fieldLabel}>Khoảng thời gian đồng bộ</div>
                  <div className={s.periodGroup}>

                    {/* Option: từ hiện tại trở về */}
                    <div
                      className={cn(s.periodRow, periodMode === 'ago' && s.periodRowActive)}
                      onClick={() => setPeriodMode('ago')}
                    >
                      <span className={cn(s.radio, periodMode === 'ago' && s.radioActive)}>
                        <span className={cn(s.radioDot, periodMode === 'ago' && s.radioDotActive)} />
                      </span>
                      <span style={{ flex: 1 }}>Từ hiện tại trở về trước</span>
                      <select
                        className={s.monthSelect}
                        value={agoMonths}
                        onChange={(e) => setAgoMonths(Number(e.target.value))}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {[1, 2, 3, 6, 12].map((m) => (
                          <option key={m} value={m}>{m} Tháng</option>
                        ))}
                      </select>
                    </div>

                    {/* Option: chọn khoảng */}
                    <div
                      className={cn(s.periodRow, periodMode === 'range' && s.periodRowActive)}
                      onClick={() => setPeriodMode('range')}
                    >
                      <span className={cn(s.radio, periodMode === 'range' && s.radioActive)}>
                        <span className={cn(s.radioDot, periodMode === 'range' && s.radioDotActive)} />
                      </span>
                      <span style={{ flex: 1 }}>Chọn khoảng thời gian cụ thể</span>
                      {periodMode === 'range' ? (
                        <div onClick={(e) => e.stopPropagation()} style={{ marginLeft: 'auto' }}>
                          <CustomDateRangePicker value={dateRange} onChange={setDateRange} placeholder="Lựa chọn ngày" />
                        </div>
                      ) : (
                        <span className={s.datePlaceholder}>Lựa chọn ngày</span>
                      )}
                    </div>

                  </div>
                </div>

                {/* Đồng bộ dữ liệu */}
                <div className={s.field}>
                  <div className={s.fieldLabel}>Đồng bộ dữ liệu</div>
                  <div className={s.pillRow}>
                    {/* Tag — always on */}
                    <span className={cn(s.pill, s.pillTag)}>
                      <svg viewBox="0 0 14 14" style={{ width: 12, height: 12, fill: 'none', stroke: '#16A34A', strokeWidth: 1.8 }}>
                        <path d="M1.5 1.5h4.5l6 6-4.5 4.5-6-6V1.5z" />
                        <circle cx="4" cy="4" r="0.8" fill="#16A34A" stroke="none" />
                      </svg>
                      Tag
                    </span>
                    {[
                      { key: 'email', label: 'Email' },
                      { key: 'phone', label: 'Số điện thoại' },
                      { key: 'birth', label: 'Ngày sinh' },
                    ].map(({ key, label }) => (
                      <span
                        key={key}
                        className={cn(s.pill, syncFields[key] && s.pillActive)}
                        onClick={() => setSyncFields((prev) => ({ ...prev, [key]: !prev[key] }))}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                  <div className={s.pillHint}>Tag luôn được đồng bộ mặc định để bạn dễ dàng lọc &amp; phân loại khách hàng sau này.</div>
                </div>

                {/* Footer */}
                <div className={s.formFooter}>
                  <span className={s.footerHint}>
                    {!canSync && 'Cần chọn Fanpage và nhập Access Token để bắt đầu'}
                  </span>
                  <Tooltip title={!canSync ? 'Vui lòng chọn Fanpage và nhập Access Token' : ''} placement="top">
                    <span>
                      <button
                        className={s.btnSync}
                        onClick={handleSync}
                        disabled={!canSync || loading}
                      >
                        {loading ? (
                          <Loading size={14} color="#fff" />
                        ) : (
                          <>
                            <SyncIcon size={15} color="#fff" />
                            Đồng bộ
                          </>
                        )}
                      </button>
                    </span>
                  </Tooltip>
                </div>

              </div>
            </div>

            {/* ── RIGHT card: result ── */}
            <div className={s.resultCard}>
              <div className={s.resultHd}>
                <ChartLineIcon />
                <span className={s.resultHdTitle}>Tổng quan kết quả đồng bộ</span>
              </div>

              {syncResult ? (
                <div style={{ padding: 16 }}>
                  <SyncChart data={syncResult} fanpageId={selectedFanpage?.value} system={selectedSystem?.value} />
                </div>
              ) : (
                <div className={s.emptyWrap}>
                  <div className={s.emptyIconCircle}>
                    <SyncIcon size={28} color="#16A34A" />
                  </div>
                  <p className={s.emptyTitle}>Chưa có dữ liệu để hiển thị</p>
                  <p className={s.emptySub}>
                    Kết quả đồng bộ (số khách hàng mới, số bản ghi trùng lặp, thời gian hoàn tất) sẽ hiện ở đây ngay sau khi bạn chạy đồng bộ.
                  </p>
                  <div className={s.stepsList}>
                    {['Chọn Fanpage cần đồng bộ', 'Nhập Access Token hợp lệ', 'Nhấn nút "Đồng bộ"'].map((text, i) => (
                      <div key={i} className={s.stepItem}>
                        <span className={s.stepNum}>{i + 1}</span>
                        {text}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ══ TAB: MÃ TÍCH HỢP ══ */}
        {activeTab === syncManager.INTEGRATION_CODE && (
          <div className={s.intCard}>
            <div className={s.intInner}>

              {/* LEFT: PRO upsell */}
              <div className={s.proPane}>
                <div className={s.proCardBadge}>
                  <svg viewBox="0 0 14 14" style={{ width: 12, height: 12, fill: 'none', stroke: '#7C3AED', strokeWidth: 1.8 }}>
                    <rect x="2" y="6" width="10" height="7" rx="1.5" />
                    <path d="M5 6V4a2 2 0 014 0v2" strokeLinecap="round" />
                  </svg>
                  TÍNH NĂNG PRO
                </div>
                <p className={s.proCardTitle}>Kết nối Bombot với hệ thống của bạn qua API</p>
                <p className={s.proCardSub}>
                  Nâng cấp gói để lấy Public Token, tích hợp Bombot vào website hoặc phần mềm nội bộ — tự động đồng bộ khách hàng, gửi tin và theo dõi chiến dịch mà không cần thao tác thủ công.
                </p>
                <div className={s.proFeatureList}>
                  {[
                    'Public Token dùng cho mọi request API không giới hạn',
                    'Tài liệu tích hợp chi tiết kèm ví dụ code mẫu',
                    'Hỗ trợ kỹ thuật ưu tiên khi tích hợp',
                  ].map((f, i) => (
                    <div key={i} className={s.proFeatureItem}>
                      <div className={s.proFeatureCheck}>
                        <svg viewBox="0 0 16 16">
                          <path d="M3 8.5l3.5 3.5L13 5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      {f}
                    </div>
                  ))}
                </div>
                <div className={s.proActions}>
                  <button className={s.btnUpgrade}>
                    <svg viewBox="0 0 14 14" style={{ width: 13, height: 13, fill: 'none', stroke: '#fff', strokeWidth: 2.2 }}>
                      <path d="M7 2v8M3 6l4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M2 12h10" strokeLinecap="round" />
                    </svg>
                    Nâng cấp ngay
                  </button>
                  <button className={s.btnCompare}>So sánh các gói →</button>
                </div>
              </div>

              {/* RIGHT: Token pane */}
              <div className={s.tokenPane}>
                <div className={s.tokenCardTitle}>Public Token</div>

                {user?.publicToken ? (
                  <div className={s.tokenBox}>
                    <span className={s.tokenVal}>{user.publicToken}</span>
                    <Tooltip
                      title={copied ? t('synchronize_management.integration_code.copied') : t('synchronize_management.integration_code.copy')}
                      placement="top"
                    >
                      <button className={s.iconBtn} onClick={handleCopyToken}>
                        <svg viewBox="0 0 16 16">
                          <rect x="5.5" y="5.5" width="8" height="9" rx="1.5" />
                          <path d="M10.5 5.5V4A1.5 1.5 0 009 2.5H4A1.5 1.5 0 002.5 4v7A1.5 1.5 0 004 12.5h1.5" />
                        </svg>
                      </button>
                    </Tooltip>
                  </div>
                ) : (
                  <div className={s.tokenLockedWrap}>
                    <div className={s.tokenBlurred}>
                      xxxx-xxxx-xxxx · xxxx-xxxx-xxxx-xxxx
                    </div>
                    <div className={s.lockIcon}>
                      <svg viewBox="0 0 16 16">
                        <rect x="3" y="7" width="10" height="8" rx="1.5" />
                        <path d="M5 7V5a3 3 0 016 0v2" strokeLinecap="round" />
                      </svg>
                    </div>
                    <div className={s.tokenLockedLabel}>Mở khóa sau khi nâng cấp</div>
                  </div>
                )}

                <div className={s.tokenDocRow}>
                  Mô tả &amp; hướng dẫn tích hợp — xem chi tiết{' '}
                  <a
                    href="https://hotro.bombot.vn/knowledgebase/huong-dan-su-dung-bo-key-tich-hop-bombot/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    TẠI ĐÂY
                  </a>
                </div>
              </div>

            </div>
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default SyncManagement;
